import { SparklesIcon } from "@heroicons/react/24/outline";
import React, { ReactNode } from "react";
import CloseButton from "../closeButton/CloseButton";
import ChatComposer from "./ChatComposer";
import ChatThread from "./ChatThread";

interface ChatPanelProps {
  title: string;
  /** Defaults to a sparkles icon; pass any node to rebrand the header. */
  icon?: ReactNode;
  onClose?: () => void;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  inputPlaceholder?: string;
  maxMessageLength?: number;
  /** Replaces the typing dots after `slowResponseNoticeDelayMs` of waiting. */
  slowResponseNotice?: string;
  slowResponseNoticeDelayMs?: number;
}

/**
 * Complete chat panel (header, thread, composer) built on assistant-ui primitives.
 * Must be rendered inside an `AssistantRuntimeProvider`.
 */
const ChatPanel = ({
  title,
  icon,
  onClose,
  emptyStateTitle,
  emptyStateSubtitle,
  inputPlaceholder,
  maxMessageLength,
  slowResponseNotice,
  slowResponseNoticeDelayMs,
}: ChatPanelProps) => {
  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-neutral-200 px-4">
        <div className="flex items-center gap-2 text-neutral-900">
          {icon ?? <SparklesIcon className="h-5 w-5 text-primary-600" />}
          <h2 className="text-base font-semibold">{title}</h2>
        </div>
        {onClose && <CloseButton onClick={onClose} />}
      </div>
      <ChatThread
        emptyStateTitle={emptyStateTitle}
        emptyStateSubtitle={emptyStateSubtitle}
        slowResponseNotice={slowResponseNotice}
        slowResponseNoticeDelayMs={slowResponseNoticeDelayMs}
      />
      <ChatComposer placeholder={inputPlaceholder} maxLength={maxMessageLength} />
    </div>
  );
};

export default ChatPanel;
